import React from 'react'
import PropTypes from 'prop-types'
import { BLOCKS, MARKS, INLINES } from '@contentful/rich-text-types'
import { renderRichText } from 'gatsby-source-contentful/rich-text'

import Heading, { HeadingSizeEnum } from '../Heading/Heading'
import { ListItem, OrderedList, UnorderedList } from '../List/List'
import { Bold, Italic, Paragraph, Underline } from '../Paragraph/Paragraph'
import { ExternalLink } from '../Link/Link'
import { GatsbyImage, getImage } from 'gatsby-plugin-image'
import styled from 'styled-components'
import alignmentMatcher from '../../interfaces/AligmentMatcher'
import Image from '../Image/Image'
import pxToRem from '../../utils/pxToRem'
import { DEFAULT_ALT_IMAGE } from '../../constants/DefaultAlts'

const options = {
  renderText: (text) =>
    text.split('\n').flatMap((text, i) => [i > 0 && <br />, text]),
  renderMark: {
    [MARKS.BOLD]: (text) => <Bold>{text}</Bold>,
    [MARKS.ITALIC]: (text) => <Italic>{text}</Italic>,
    [MARKS.UNDERLINE]: (text) => <Underline>{text}</Underline>,
    [MARKS.CODE]: (text) => <code>{text}</code>,
  },
  renderNode: {
    [BLOCKS.EMBEDDED_ASSET]: (node) => {
      if (node.data.target) {
        const { gatsbyImageData, description } = node.data.target
        return (
          <GatsbyImage
            image={getImage(gatsbyImageData)}
            alt={description || DEFAULT_ALT_IMAGE}
          />
        )
      }
    },
    [BLOCKS.EMBEDDED_ENTRY]: (node) => {
      if (node.data.target?.__typename === 'ContentfulComponentRichText') {
        const alignment = alignmentMatcher[node.data.target.layoutContent]
        const references = node.data.target.content?.references?.map((ref) => {
          if (ref.__typename === 'ContentfulContentMedia') {
            const media = ref
            const alt = media?.cloudinaryAssetDesktop
              ? media?.cloudinaryAssetDesktop[0].context?.custom.alt
              : media.altText?.altText || DEFAULT_ALT_IMAGE

            const path = media?.cloudinaryAssetDesktop
              ? media.cloudinaryAssetDesktop[0].url
              : media.contentfulAsset
              ? media.contentfulAsset.gatsbyImageData
              : null
            return <StyledImage alt={alt} absolutePath={path} />
          }
        })

        return <StyledDiv $alignment={alignment}>{references}</StyledDiv>
      }
      if (node.data.target?.__typename === 'contentulContentMedia') {
        const media = node.data.target
        // TODO : retrieve mediaFormat to display image or video
        //media.cloudinaryAssetDesktop[0].resource_type === MediaTypeEnum.image
        return (
          <StyledImage
            alt={media.altText?.altText || DEFAULT_ALT_IMAGE}
            absolutePath={media.cloudinaryAssetDesktop[0].url}
          />
        )
      }
    },
    [BLOCKS.HEADING_1]: (node, children) => (
      <Heading label={children} size={HeadingSizeEnum.h1} />
    ),
    [BLOCKS.HEADING_2]: (node, children) => (
      <Heading label={children} size={HeadingSizeEnum.h2} />
    ),
    [BLOCKS.HEADING_3]: (node, children) => (
      <Heading label={children} size={HeadingSizeEnum.h3} />
    ),
    [BLOCKS.HEADING_4]: (node, children) => (
      <Heading label={children} size={HeadingSizeEnum.h4} />
    ),
    [BLOCKS.HEADING_5]: (node, children) => (
      <Heading label={children} size={HeadingSizeEnum.h5} />
    ),
    [BLOCKS.HEADING_6]: (node, children) => (
      <Heading label={children} size={HeadingSizeEnum.h6} />
    ),
    [BLOCKS.HR]: () => <hr />,
    [BLOCKS.PARAGRAPH]: (node, children) => {
      return <Paragraph>{children}</Paragraph>
    },
    [BLOCKS.QUOTE]: (children) => (
      <blockquote>
        <>"{children.content[0].content[0].value}"</>
      </blockquote>
    ),

    [BLOCKS.LIST_ITEM]: (node, children) => <ListItem>{children}</ListItem>,
    [BLOCKS.UL_LIST]: (node, children) => (
      <UnorderedList>{children}</UnorderedList>
    ),
    [BLOCKS.OL_LIST]: (node, children) => <OrderedList>{children}</OrderedList>,

    [INLINES.EMBEDDED_ENTRY]: (node, children) => {
      return
    },
    [INLINES.HYPERLINK]: (node, children) => {
      const { uri } = node.data
      if (uri.includes('iframe')) {
        return (
          <div>
            <div dangerouslySetInnerHTML={{ __html: uri }} />
          </div>
        )
      } else {
        return (
          <ExternalLink target="_blank" rel="noreferrer" href={uri}>
            {children}
          </ExternalLink>
        )
      }
    },
  },
}

const RichText = (props) => {
  const { bodyRichText, className } = props
  return (
    <div className={className}>
      {bodyRichText && renderRichText(bodyRichText, options)}
    </div>
  )
}

RichText.propTypes = {
  bodyRichText: PropTypes.object,
  className: PropTypes.any,
}

export default RichText

export const StyledRichText = styled(RichText)`
  text-align: ${({ $alignment }) => ($alignment ? $alignment : 'left')};
`

export const StyledDiv = styled.div`
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: ${({ $alignment }) => {
    switch ($alignment) {
      case 'left':
      default:
        return 'start'
      case 'right':
        return 'end'
      case 'center':
        return 'center'
    }
  }};
  display: flex;
`

export const StyledImage = styled(Image)`
  margin: ${pxToRem(10)};
  object-fit: contain;
`
